import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import AdminLayout from "../layouts/adminLayout/AdminLayout";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getUser,
  getPermissions,
} from "../config/api/userApi";

export default function User() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [permissionOptions, setPermissionOptions] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    permissions: [],
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);

      const res = await getUsers({
        page: page + 1,
        per_page: pageSize,
        search: appliedSearch || undefined,
      });

      setRows(res?.data?.data || []);
      setRowCount(res?.data?.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedSearch]);

  const fetchPermissionList = async () => {
    try {
      const res = await getPermissions();
      setPermissionOptions(res?.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load permissions");
    }
  };

  useEffect(() => {
    fetchPermissionList();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(0);
    setAppliedSearch(search);
  };

  const handleOpenCreate = () => {
    setEditId(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      permissions: [],
    });
    setOpenModal(true);
  };

  const handleOpenEdit = async (row) => {
    try {
      setLoading(true);
      const res = await getUser(row.id);
      const data = res?.data;

      setEditId(data?.id);
      setFormData({
        name: data?.name || "",
        email: data?.email || "",
        password: "",
        permissions: data?.permissions || [],
      });
      setOpenModal(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditId(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      permissions: [],
    });
  };

  const handlePermissionChange = (permission) => {
    setFormData((prev) => {
      const exists = prev.permissions.includes(permission);

      return {
        ...prev,
        permissions: exists
          ? prev.permissions.filter((item) => item !== permission)
          : [...prev.permissions, permission],
      };
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error("Name is required");
        return;
      }

      if (!formData.email.trim()) {
        toast.error("Email is required");
        return;
      }

      if (!editId && !formData.password.trim()) {
        toast.error("Password is required");
        return;
      }

      setSaveLoading(true);

      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        permissions: formData.permissions,
      };

      if (editId) {
        await updateUser(editId, {
          ...payload,
          _method: "PUT",
        });
        toast.success("User updated successfully");
      } else {
        await createUser(payload);
        toast.success("User created successfully");
      }

      handleCloseModal();
      fetchUsers();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save user");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete?")) return;

    try {
      await deleteUser(id);
      toast.success("User deleted successfully");

      const isLastRowOnPage = rows.length === 1 && page > 0;
      if (isLastRowOnPage) {
        setPage((prev) => prev - 1);
      } else {
        fetchUsers();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete user");
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
    },
    {
      field: "name",
      headerName: "Name",
      width: 180,
    },
    {
      field: "email",
      headerName: "Email",
      width: 220,
    },
    {
      field: "permissions",
      headerName: "Permissions",
      width: 350,
      sortable: false,
      renderCell: (params) => {
        const permissions = params.row?.permissions || [];
        return (
          <Typography variant="body2">
            {permissions.length ? permissions.join(", ") : "-"}
          </Typography>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box className="flex gap-2">
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenEdit(params.row)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout title={"Users"}>
      <Box className="p-6 bg-white !rounded">
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <TextField
            label="Search by name or email"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>

          <Button variant="outlined" onClick={handleOpenCreate}>
            Add User
          </Button>
        </div>

        <div style={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              if (model.page !== page) setPage(model.page);
              if (model.pageSize !== pageSize) {
                setPageSize(model.pageSize);
                setPage(0);
              }
            }}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
          />
        </div>

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>{editId ? "Edit User" : "Add User"}</DialogTitle>

          <DialogContent>
            <Box className="grid grid-cols-1 gap-4 mt-3">
              <TextField
                label="Name"
                size="small"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                fullWidth
              />

              <TextField
                label="Email"
                size="small"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                fullWidth
              />

              <TextField
                label={editId ? "Password (optional)" : "Password"}
                size="small"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                fullWidth
              />

              <Box>
                <Typography variant="subtitle1" className="mb-2">
                  Permissions
                </Typography>

                <FormGroup>
                  {permissionOptions.map((permission) => (
                    <FormControlLabel
                      key={permission}
                      control={
                        <Checkbox
                          checked={formData.permissions.includes(permission)}
                          onChange={() => handlePermissionChange(permission)}
                        />
                      }
                      label={permission}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saveLoading}
            >
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}